import { Badge, Card, Link, SkeletonBodyText, Text } from '@shopify/polaris';
import { CardHeading } from '../../../../shared/components/cards/CardHeading';
import { formatEur } from './chartTheme';

/*
  What needs attention this week (from /api/dashboard/watchlist): cash outlook, overdue customers,
  invoices overdue > 90 days, old orders still in the backlog, supplier documents waiting for approval,
  supplier invoices we are late with, open credit notes. Ordered by severity; every item links to the
  place where it can be acted upon. Laid out for a narrow column next to the cash planning card.
*/

const TONES = { critical: 'critical', warning: 'warning', info: 'info', ok: 'success' };
const LABELS = { critical: 'Critical', warning: 'Watch', info: 'Note', ok: 'OK' };
const SUMMARY = { critical: 'critical', warning: 'to watch', info: 'notes', ok: 'ok' };

export default function WatchlistCard({ watchlist, loading, error }) {
  const items = watchlist?.items || [];
  const summary = Object.keys(SUMMARY)
    .map((key) => ({ key, count: items.filter((item) => item.severity === key).length }))
    .filter((entry) => entry.count > 0)
    .map((entry) => `${entry.count} ${SUMMARY[entry.key]}`)
    .join(' · ');

  return (
    <Card>
      <CardHeading title="Watchlist" />
      <Text as="p" variant="bodySm" tone="subdued">
        {error ? error : loading || !watchlist ? 'Checking what needs attention…' : items.length ? summary : 'Nothing needs attention this week.'}
      </Text>
      {!error && (loading || !watchlist) ? (
        <div style={{ marginTop: 12 }}>
          <SkeletonBodyText lines={6} />
        </div>
      ) : null}
      {items.length ? (
        <div style={{ marginTop: 8, maxHeight: 640, overflowY: 'auto' }}>
          {items.map((item) => (
            <div key={item.key} style={{ padding: '10px 0', borderTop: '1px solid var(--p-color-border-secondary, #ebebeb)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <Badge tone={TONES[item.severity] || 'info'}>{LABELS[item.severity] || item.severity}</Badge>
                {item.link ? (
                  <Link removeUnderline url={item.link}>
                    Open
                  </Link>
                ) : null}
              </div>
              <div style={{ marginTop: 6 }}>
                <Text as="p" variant="bodySm" fontWeight="semibold">
                  {item.title}
                </Text>
                {item.detail ? (
                  <Text as="p" variant="bodySm" tone="subdued">
                    {item.detail}
                  </Text>
                ) : null}
              </div>
              {item.links?.length ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 10px', marginTop: 4, fontSize: 12 }}>
                  {item.links.map((link) => (
                    <span key={link.url} style={{ whiteSpace: 'nowrap' }}>
                      <Link removeUnderline url={link.url}>
                        {link.label}
                      </Link>
                      {link.amount ? (
                        <Text as="span" variant="bodySm" tone="subdued">
                          {` ${formatEur(link.amount)}${link.days ? ` · ${link.days} d` : ''}`}
                        </Text>
                      ) : null}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </Card>
  );
}
